import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../app/api';
import { setAlert } from '../alert/alertSlice';

// Initial state
const initialState = {
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  user: null,
  error: null
};

// Register user
export const register = createAsyncThunk(
  'auth/register',
  async ({ username, email, password }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post('/auth/register', { username, email, password });
      return res.data;
    } catch (err) {
      const message = 
        err.response?.data?.error || 
        err.response?.data?.errors?.[0]?.msg || 
        'Server Error';
      
      dispatch(setAlert({ msg: message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

// Login user
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      return res.data;
    } catch (err) {
      const message = 
        err.response?.data?.error || 
        err.response?.data?.errors?.[0]?.msg || 
        'Server Error';
      
      dispatch(setAlert({ msg: message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

// Load user
export const loadUser = createAsyncThunk(
  'auth/loadUser',
  async (_, { rejectWithValue, getState }) => {
    try {
      // Only attempt to load user if we have a token
      if (!getState().auth.token) {
        return rejectWithValue('No token');
      }
      
      const res = await api.get('/auth/me');
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || 
        err.response?.data?.errors?.[0]?.msg || 
        'Server Error'
      );
    }
  }
);

// Logout
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch }) => {
    try {
      await api.get('/auth/logout');
      dispatch(setAlert({ msg: 'Logged out successfully', type: 'success' }));
    } catch (err) {
      // Even if server logout fails, we still want to clear local auth state
      console.error('Logout error:', err);
    }
  }
);

// Add bookmark to user
export const saveBookmark = createAsyncThunk(
  'auth/saveBookmark',
  async (paperId, { rejectWithValue }) => {
    try {
      // This would make a real API call in a complete implementation
      // For now, just return the paperId to simulate success
      console.log('Saving bookmark:', paperId);
      return { paper: paperId };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error saving bookmark');
    }
  }
);

// Remove bookmark from user
export const removeBookmark = createAsyncThunk(
  'auth/removeBookmark',
  async (paperId, { rejectWithValue }) => {
    try {
      // This would make a real API call in a complete implementation
      // For now, just return the paperId to simulate success
      console.log('Removing bookmark:', paperId);
      return paperId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error removing bookmark');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.data;
        state.token = action.payload.token;
        localStorage.setItem('token', action.payload.token);
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        localStorage.removeItem('token');
      })
      
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.data;
        state.token = action.payload.token;
        localStorage.setItem('token', action.payload.token);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        localStorage.removeItem('token');
      })
      
      // Load User
      .addCase(loadUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.data;
      })
      .addCase(loadUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        localStorage.removeItem('token');
      })
      
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        localStorage.removeItem('token');
      })
      
      // Save bookmark
      .addCase(saveBookmark.fulfilled, (state, action) => {
        if (state.user && state.user.bookmarks) {
          state.user.bookmarks.push(action.payload);
        } else if (state.user) {
          state.user.bookmarks = [action.payload];
        }
      })
      
      // Remove bookmark
      .addCase(removeBookmark.fulfilled, (state, action) => {
        if (state.user && state.user.bookmarks) {
          state.user.bookmarks = state.user.bookmarks.filter(
            bookmark => bookmark.paper !== action.payload
          );
        }
      });
  }
});

export const { clearError } = authSlice.actions;

export default authSlice.reducer; 